/**
 * api.endpoints.tsx — THE single source of truth for every backend URL.
 *
 * Nothing else in the app may hard-code an endpoint. Services import the
 * relevant URL from here. Two backends:
 *   • Platform API (auth)   — login/signup/session only.
 *   • Billing API (billing) — everything else (incl. contacts via Backbone → CRM).
 *
 * Base URLs come from process.env, injected at BUILD TIME by webpack's
 * DefinePlugin (web/webpack.config.ts); local dev falls back to localhost.
 * The DevOps pipeline sets BILLING_API_URL per environment:
 *   dev     → https://bill.api.dev.softknack.com
 *   staging → https://bill.api.staging.softknack.com
 *   prod    → https://bill.api.softknack.com
 * To switch environments, change ONLY the env vars — never the paths below.
 */

// ── PLATFORM API base (auth, identity) ──────────────────────────────────────
export const PLATFORM_API = process.env.PLATFORM_API_URL || 'http://localhost:3013';

// ── BILLING API base (documents, payments, contacts, taxes, …) ──────────────
export const BILLING_API = process.env.BILLING_API_URL || 'http://localhost:3014';

// ── Versioned prefixes ──────────────────────────────────────────────────────
const P = `${PLATFORM_API}/v1`; // e.g. http://localhost:3013/v1
const B = `${BILLING_API}/api/billing/v1`; // e.g. http://localhost:3014/api/billing/v1

/* ───────────────────────── Platform (session only) ─────────────────────────
 * Login / signup / verification / password-reset all live on the centralized
 * SK Platform (platform-fe). This app keeps only session-consumption endpoints:
 * logout, silent token refresh + bearer fetch (for cross-service Billing calls),
 * and the /me session probe. */
export const Platform_Logout_Url = `${P}/auth/logout`; // POST
export const Platform_Refresh_Token_Url = `${P}/auth/refresh`; // POST
export const Platform_Token_Url = `${P}/auth/token`; // GET (fetch bearer for cross-service calls)
export const Platform_Me_Url = `${P}/me`; // GET

/* ───────────────────────── Billing: contacts (via Backbone → CRM) ───────────────────────── */
export const Billing_Contacts_Url = `${B}/contacts`; // GET (?type=&q=) | POST
export const Billing_Contact_Url = (id: string) => `${B}/contacts/${id}`; // GET | PATCH | DELETE (soft)
export const Billing_Contact_Phone_Url = (id: string) => `${B}/contacts/${id}/phone`; // POST (audited phone change)

/* ───────────────────────── Billing: org/profile/config ───────────────────────── */
export const Billing_RegionPacks_Url = `${B}/region-packs`; // GET
export const Billing_RegionPacks_States_Url = `${B}/region-packs/states`; // GET
export const Billing_OrgProfile_Url = `${B}/org-profile`; // GET
export const Billing_OrgProfile_Activate_Url = `${B}/org-profile/activate`; // POST
// Tax registrations (CC-001 CH-2.3) — the SSoT for GSTIN/TRN/LUT/…, replacing
// the single org_billing_profiles.gstin column. Many per org, each with its own
// jurisdiction and effectivity.
export const Billing_TaxRegistrations_Url = `${B}/org-profile/tax-registrations`; // GET | POST
export const Billing_TaxRegistration_End_Url = (id: string) =>
  `${B}/org-profile/tax-registrations/${id}/end`; // POST
export const Billing_Currencies_Url = `${B}/currencies`; // GET
export const Billing_Currency_Url = (code: string) => `${B}/currencies/${code}`; // PUT
export const Billing_ExchangeRates_Url = `${B}/exchange-rates`; // GET | POST

/* ───────────────────────── Billing: documents ───────────────────────── */
export const Billing_Documents_Url = `${B}/documents`; // GET (?type=&status=) | POST
export const Billing_Document_Url = (id: string) => `${B}/documents/${id}`; // GET
export const Billing_Document_Events_Url = (id: string) => `${B}/documents/${id}/events`; // GET
export const Billing_Document_Pdf_Url = (id: string) => `${B}/documents/${id}/pdf`; // GET
export const Billing_Document_Issue_Url = (id: string) => `${B}/documents/${id}/issue`; // POST
// Save an opened DRAFT in place — header + lines, same id (the editor's "Save draft").
export const Billing_Document_Draft_Url = (id: string) => `${B}/documents/${id}/draft`; // PUT
export const Billing_Document_Void_Url = (id: string) => `${B}/documents/${id}/void`; // POST
export const Billing_Document_WriteOff_Url = (id: string) => `${B}/documents/${id}/write-off`; // POST
export const Billing_Document_Convert_Url = (id: string) => `${B}/documents/${id}/convert`; // POST
export const Billing_Document_Accept_Url = (id: string) => `${B}/documents/${id}/accept`; // POST
export const Billing_Document_Decline_Url = (id: string) => `${B}/documents/${id}/decline`; // POST
export const Billing_Document_Deliver_Url = (id: string) => `${B}/documents/${id}/deliver`; // POST
export const Billing_Document_ResolvePlaceholder_Url = (id: string) =>
  `${B}/documents/${id}/resolve-placeholder`; // POST
export const Billing_Document_LandedCosts_Url = (id: string) => `${B}/documents/${id}/landed-costs`; // POST
export const Billing_Document_LandedCost_Url = (id: string, lcId: string) =>
  `${B}/documents/${id}/landed-costs/${lcId}`; // PATCH
export const Billing_Documents_AiDraft_Url = `${B}/documents/ai-draft`; // POST

/* ───────────────────────── Billing: payments ───────────────────────── */
export const Billing_Payments_Url = `${B}/payments`; // GET | POST
export const Billing_Payment_Url = (id: string) => `${B}/payments/${id}`; // GET
export const Billing_Payment_Allocate_Url = (id: string) => `${B}/payments/${id}/allocate`; // POST
export const Billing_Payment_Refund_Url = (id: string) => `${B}/payments/${id}/refund`; // POST
export const Billing_Payments_Withholding_Url = `${B}/payments/withholding`; // GET
// Superseded: the server renamed this to /payments/links (CR-3) and did not keep an alias.
export const Billing_Payments_RazorpayLinks_Url = `${B}/payments/razorpay/links`; // POST
export const Billing_Payments_Links_Url = `${B}/payments/links`; // POST

/* ───────────────────────── Billing: journals & ledger ───────────────────────── */
export const Billing_Journals_Url = `${B}/journals`; // GET | POST
export const Billing_Journal_Url = (id: string) => `${B}/journals/${id}`; // GET
export const Billing_Journal_Reverse_Url = (id: string) => `${B}/journals/${id}/reverse`; // POST
export const Billing_Ledger_TrialBalance_Url = `${B}/ledger/trial-balance`; // GET
export const Billing_Ledger_Account_Url = (id: string) => `${B}/ledger/accounts/${id}`; // GET

/* ───────────────────────── Billing: banking & reconciliation ───────────────────────── */
export const Billing_Banking_Accounts_Url = `${B}/banking/accounts`; // GET | POST
export const Billing_Banking_AccountImports_Url = (id: string) =>
  `${B}/banking/accounts/${id}/imports`; // POST
export const Billing_Banking_Lines_Url = `${B}/banking/lines`; // GET (?status=)
export const Billing_Banking_Line_Resolve_Url = (id: string) => `${B}/banking/lines/${id}/resolve`; // POST
export const Billing_Banking_Rules_Url = `${B}/banking/rules`; // GET | POST

/* ───────────────────────── Billing: periods ───────────────────────── */
export const Billing_Periods_Url = `${B}/periods`; // GET | POST
export const Billing_Period_Close_Url = (id: string) => `${B}/periods/${id}/close`; // POST

/* ───────────────────────── Billing: taxes ───────────────────────── */
export const Billing_Tax_Rates_Url = `${B}/taxes/rates`; // GET | POST
export const Billing_Tax_Rate_Url = (id: string) => `${B}/taxes/rates/${id}`; // PATCH
export const Billing_Tax_Groups_Url = `${B}/taxes/groups`; // GET | POST
export const Billing_Tax_Group_Url = (id: string) => `${B}/taxes/groups/${id}`; // PATCH
export const Billing_Tax_Preview_Url = `${B}/taxes/preview`; // POST

/* ───────────────────────── Billing: compliance / e-invoice ───────────────────────── */
export const Billing_Einvoice_Url = (docId: string) => `${B}/compliance/einvoice/${docId}`; // GET
export const Billing_Einvoice_Export_Url = (docId: string) =>
  `${B}/compliance/einvoice/${docId}/export`; // POST
export const Billing_Einvoice_BulkExport_Url = `${B}/compliance/einvoice/export`; // POST
export const Billing_Einvoice_Irn_Url = (docId: string) => `${B}/compliance/einvoice/${docId}/irn`; // POST
export const Billing_Gstr1_Url = `${B}/compliance/gstr1`; // GET (?period=)
export const Billing_Gstr3b_Url = `${B}/compliance/gstr3b`; // GET (?period=)
export const Billing_VatSummary_Url = `${B}/compliance/vat-summary`; // GET (?period=)

/* ───────────────────────── Billing: reports ───────────────────────── */
export const Billing_Report_TrialBalance_Url = `${B}/reports/trial-balance`; // GET
export const Billing_Report_Pnl_Url = `${B}/reports/pnl`; // GET
export const Billing_Report_BalanceSheet_Url = `${B}/reports/balance-sheet`; // GET
export const Billing_Report_CashFlow_Url = `${B}/reports/cash-flow`; // GET
export const Billing_Report_Aging_Url = `${B}/reports/aging`; // GET (?side=AR|AP)

/* ───────────────────────── Billing: recurring schedules ───────────────────────── */
export const Billing_RecurringSchedules_Url = `${B}/recurring-schedules`; // GET | POST
export const Billing_RecurringSchedule_Url = (id: string) => `${B}/recurring-schedules/${id}`; // PATCH

/* ───────────────────────── Billing: migration ───────────────────────── */
export const Billing_Migration_Batches_Url = `${B}/migration/batches`; // GET | POST
export const Billing_Migration_Batch_Url = (id: string) => `${B}/migration/batches/${id}`; // GET
export const Billing_Migration_OpeningTemplate_Url = `${B}/migration/opening-template`; // GET
export const Billing_Migration_OpeningBalances_Url = (id: string) =>
  `${B}/migration/batches/${id}/opening-balances`; // POST
export const Billing_Migration_OpenDocuments_Url = (id: string) =>
  `${B}/migration/batches/${id}/open-documents`; // POST
export const Billing_Migration_GoLive_Url = (id: string) => `${B}/migration/batches/${id}/go-live`; // POST
export const Billing_Migration_ColumnMapping_Url = `${B}/migration/column-mapping/suggest`; // POST

/* ───────────────────────── Billing: AI ───────────────────────── */
export const Billing_Ai_ExtractDocument_Url = `${B}/ai/extract-document`; // POST
export const Billing_Ai_DunningDraft_Url = `${B}/ai/dunning-draft`; // POST
export const Billing_Ai_Ask_Url = `${B}/ai/ask`; // POST
